import Icon from '@ant-design/icons';
import { Select } from 'antd';
import { BiRightArrowAlt } from 'react-icons/bi';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import ConsumerMap from '../../../components/map/ConsumerMap';
import {
  districtBorderStyleFunction,
  getColorByIntensity,
  normalizeString,
} from '../../../utils/defaultMapFunctions';
import { forwardRef } from 'react';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENVIRONMENT_CONFIG } from '../../../constants/environmentConfig';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import fetcher from '../../../helpers/fetcher';
import {
  CityLocationFeature,
  CityLocationProperties,
  DistrictLocationFeature,
  DistrictLocationProperties,
} from '../../../components/map/iConsumerMap';
import { SpinLoader, SpinTypes } from '../../../components/common/SpinLoader';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import type { Basket } from '../../../types/basket';
import type { Presentation } from '../../../types/presentation';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';

interface RegionalPriceProps {
  t: (key: string) => string;
  ref: React.RefObject<HTMLDivElement>;
}

interface RegionalCityPrice {
  locationId: number;
  city: string;
  price: number;
  avgPrice?: number;
  inflation?: number;
  inspectionCount: number;
  establishmentCount: number;
}

interface RegionalDistrictPrice {
  district: string;
  price: number;
  avgPrice?: number;
  inflation?: number;
  inspectionCount: number;
  establishmentCount: number;
}

const normalizeRegionalPrice = <
  T extends {
    price?: number;
    avgPrice?: number;
  },
>(
  item: T
): T & { price: number } => ({
  ...item,
  price: Number(item.price ?? item.avgPrice ?? 0),
});

const RegionalPrice = forwardRef<HTMLDivElement, RegionalPriceProps>(({ t }, ref) => {
  const navigate = useNavigate();
  const [basketLoading, setBasketLoading] = useState<boolean>(false);
  const [presentationLoading, setPresentationLoading] = useState<boolean>(false);
  const [priceLoading, setPriceLoading] = useState<boolean>(false);

  const [baskets, setBaskets] = useState<Array<Basket & { productCount: number }>>([]);
  const [selectedBasket, setSelectedBasket] = useState<Basket | undefined>(undefined);

  const [presentations, setPresentations] = useState<Array<Presentation>>([]);
  const [selectedPresentation, setSelectedPresentation] = useState<Presentation | undefined>(
    undefined
  );

  const [regionalPrice, setRegionalPrice] = useState<Array<RegionalCityPrice> | null>(null);
  const [maxRegionalPrice, setMaxRegionalPrice] = useState(0);

  const [regionalDistrictPrice, setRegionalDistrictPrice] =
    useState<Array<RegionalDistrictPrice> | null>(null);
  const [maxRegionalDistrictPrice, setMaxRegionalDistrictPrice] = useState(0);
  const { isOnline } = useIsOnlineContext();

  const getDefaultPriceRange = () => {
    const endTime = Date.now();
    const startTime = endTime - 7 * 24 * 60 * 60 * 1000;

    return {
      startTime: startTime.toString(),
      endTime: endTime.toString(),
    };
  };

  const getLocationPriceRequest = useCallback(
    (type: 'city' | 'district') => {
      const { startTime, endTime } = getDefaultPriceRange();
      const queryParams: Record<string, string | string[]> = {
        startTime,
        endTime,
        basketId: selectedBasket?.id.toString() ?? '',
      };

      if (selectedPresentation) {
        queryParams.productIds = [selectedPresentation.id.toString()];
      }

      return {
        endpoint: `${API_ENDPOINTS.PRICE.GET_ALL_PUBLIC_PRICES_BY}/${type}`,
        queryParams,
      };
    },
    [selectedBasket?.id, selectedPresentation]
  );

  const fetchBasketsData = useCallback(async () => {
    try {
      setBasketLoading(true);
      const basketsData = await referenceDataSyncService.getBaskets();
      setBaskets(basketsData);

      if (!selectedBasket) {
        const basicBasket = await referenceDataSyncService.getBasicFoodBasket();
        if (basicBasket) {
          setSelectedBasket(basicBasket);
        }
      }
    } catch (error) {
      console.error('Failed to load baskets from IndexedDB:', error);
    } finally {
      setBasketLoading(false);
    }
  }, [selectedBasket]);

  const fetchPresentationsData = useCallback(async () => {
    if (!selectedBasket) return;
    try {
      setPresentationLoading(true);
      const presentationsData = await referenceDataSyncService.getPresentationsForFilters(
        selectedBasket?.id
      );

      setPresentations(presentationsData);
    } catch (error) {
      console.error('Failed to load presentations from IndexedDB:', error);
    } finally {
      setPresentationLoading(false);
    }
  }, [selectedBasket]);

  const fetchCityData = useCallback(async () => {
    if (!selectedBasket) return;
    setPriceLoading(true);

    const { endpoint, queryParams } = getLocationPriceRequest('city');

    const response = await fetcher.get(
      endpoint,
      queryParams,
      {},
      ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
    );
    const dataList = (response.data.data || []).map(normalizeRegionalPrice);
    setRegionalPrice(dataList);
    setMaxRegionalPrice(Math.max(...dataList.map((item: RegionalCityPrice) => item.price), 0));
    setPriceLoading(false);
  }, [getLocationPriceRequest, selectedBasket]);

  const fetchDistrictData = useCallback(async () => {
    if (!selectedBasket) return;
    setPriceLoading(true);

    const { endpoint, queryParams } = getLocationPriceRequest('district');

    const response = await fetcher.get(
      endpoint,
      queryParams,
      {},
      ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
    );
    const dataList = (response.data.data || []).map(normalizeRegionalPrice);
    setRegionalDistrictPrice(dataList);
    setMaxRegionalDistrictPrice(
      Math.max(...dataList.map((item: RegionalDistrictPrice) => item.price), 0)
    );
    setPriceLoading(false);
  }, [getLocationPriceRequest, selectedBasket]);

  const districtStyleFunction = useCallback(
    (feature: DistrictLocationFeature | undefined) => {
      const district = normalizeString(feature?.properties.district || '');
      const price =
        regionalDistrictPrice?.find((item) => normalizeString(item.district) === district)?.price ??
        0;

      return {
        fillColor: price ? getColorByIntensity(price, maxRegionalDistrictPrice) : '#ccc',
        fillOpacity: 0.6,
        color: '#fff',
        weight: 1,
      };
    },
    [regionalDistrictPrice, maxRegionalDistrictPrice]
  );

  const cityStyleFunction = useCallback(
    (feature: CityLocationFeature | undefined) => {
      const locationId = feature?.properties.locationId || '';
      const price = regionalPrice?.find((item) => item.locationId === locationId)?.price ?? 0;
      return {
        fillColor: price ? getColorByIntensity(price, maxRegionalPrice) : '#ccc',
        fillOpacity: 0.6,
        color: '#fff',
        weight: 1,
      };
    },
    [regionalPrice, maxRegionalPrice]
  );

  const popupContentFunction = useCallback(
    (feature: CityLocationFeature | DistrictLocationFeature): string => {
      if ('city' in feature.properties) {
        const props = feature.properties as CityLocationProperties;
        const item = regionalPrice?.find((item) => item.locationId === props.locationId);

        return `
          <div>
            <h3 class="text-2xl font-semibold">${item?.city || props.city}</h3>
            <p><strong>${t('averagePriceLabel')}:</strong> ${item?.price !== undefined ? item.price : 'N/A'}</p>
            <p><strong>${t('inspections')}:</strong> ${item?.inspectionCount || 0}</p>
            <p><strong>${t('establishments')}:</strong> ${item?.establishmentCount || 0}</p>
            <p><strong>${t('departments')}:</strong> ${props.district}</p>
          </div>
        `;
      } else {
        const props = feature.properties as DistrictLocationProperties;
        const item = regionalDistrictPrice?.find(
          (item) => normalizeString(item.district) === normalizeString(props.district)
        );

        return `
          <div>
            <h3 class="text-2xl font-semibold">${item?.district || props.district}</h3>
            <p><strong>${t('averagePriceLabel')}:</strong> ${item?.price !== undefined ? item.price : 'N/A'}</p>
            <p><strong>${t('inspections')}:</strong> ${item?.inspectionCount || 0}</p>
            <p><strong>${t('establishments')}:</strong> ${item?.establishmentCount || 0}</p>
          </div>
        `;
      }
    },
    [regionalPrice, regionalDistrictPrice, t]
  );

  useEffect(() => {
    fetchBasketsData();
  }, [fetchBasketsData]);

  useEffect(() => {
    setSelectedPresentation(undefined);
    fetchPresentationsData();
  }, [selectedBasket, fetchPresentationsData]);

  useEffect(() => {
    fetchCityData();
    fetchDistrictData();
  }, [selectedBasket, selectedPresentation, fetchCityData, fetchDistrictData]);

  return (
    <div className="section regional-price mx-10" ref={ref}>
      <h1 className="section-title">{t('regionalPriceTitle')}</h1>
      <div className="filters flex flex-wrap gap-4 lg:flex-nowrap">
        <Select
          className="min-w-0 flex-1 [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
          placeholder={t('filter_basket')}
          allowClear
          value={selectedBasket?.id || undefined}
          onChange={(value) => {
            const basket = baskets.find((b) => b.id === value);
            setSelectedBasket(basket);
          }}
          loading={basketLoading}
          options={baskets.map((basket) => ({
            label: basket.name,
            value: basket.id,
          }))}
        />
        {selectedBasket && (
          <Select
            className="min-w-0 flex-1 [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
            placeholder={t('filter_product')}
            allowClear
            value={selectedPresentation?.id || undefined}
            onChange={(value) => {
              const presentation = presentations.find((p) => p.id === value);
              setSelectedPresentation(presentation);
            }}
            loading={presentationLoading}
            options={presentations.map((presentation) => ({
              label: presentation.name,
              value: presentation.id,
            }))}
          />
        )}
      </div>
      <div className="mt-6">
        <p>{t('noteRegardingExploreRegionalPriceMap')}</p>
      </div>
      <div
        className="map-container relative flex h-auto items-center justify-center text-6xl text-primary-gray-300"
        id="regional-map"
      >
        <Suspense fallback={isOnline ? <SpinLoader type={SpinTypes.CENTER} /> : null}>
          <ConsumerMap
            key={`${selectedBasket?.id}-${selectedPresentation?.refId || 'basket'}`}
            districtStyleFunction={districtStyleFunction}
            cityStyleFunction={cityStyleFunction}
            districtBorderStyleFunction={districtBorderStyleFunction}
            popupContentFunction={popupContentFunction}
          />
        </Suspense>
        {priceLoading && isOnline && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <SpinLoader type={SpinTypes.CENTER} />
          </div>
        )}
      </div>
      <div className="flex items-center justify-center">
        <PrimaryButton
          handleOnClick={() => {
            navigate('/citizenPortal');
            // Scroll to the section after navigation
            setTimeout(() => {
              const element = document.getElementById('price-map-section');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          }}
          disabled={!isOnline}
        >
          <span>
            {t('regianalPriceCTA')}{' '}
            <Icon>
              <BiRightArrowAlt />
            </Icon>
          </span>
        </PrimaryButton>
      </div>
    </div>
  );
});

export default RegionalPrice;
